import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// Shared locale dosyaları
import commonTr from './locales/shared/tr/common.json';
import commonEn from './locales/shared/en/common.json';
import authTr from './locales/shared/tr/auth.json';
import authEn from './locales/shared/en/auth.json';
import enumsTr from './locales/shared/tr/enums.json';
import enumsEn from './locales/shared/en/enums.json';

// Panel-spesifik locale dosyaları
import userTr from './locales/user/tr.json';
import userEn from './locales/user/en.json';
import companyTr from './locales/company/tr.json';
import companyEn from './locales/company/en.json';
import adminTr from './locales/admin/tr.json';
import adminEn from './locales/admin/en.json';

// Panel locale haritası
const panelResources: Record<string, { tr: object; en: object }> = {
  user: { tr: userTr, en: userEn },
  company: { tr: companyTr, en: companyEn },
  admin: { tr: adminTr, en: adminEn },
};

export type PanelType = 'user' | 'company' | 'admin';
export type SupportedLanguage = 'tr' | 'en';

/**
 * Dil tespiti: URL param > cookie > localStorage > default
 * Website'den yönlendirmelerde ?lang=en gibi parametre gelebilir
 * Website next-intl cookie'si NEXT_LOCALE olarak set edilir
 */
function detectLanguage(): SupportedLanguage {
  // 1. URL parametresi kontrol (?lang=en)
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const langParam = urlParams.get('lang');
    if (langParam === 'en' || langParam === 'tr') {
      // URL'den gelen dili localStorage'a kaydet
      localStorage.setItem('language', langParam);
      // URL'den lang parametresini temizle
      urlParams.delete('lang');
      const newUrl = urlParams.toString()
        ? `${window.location.pathname}?${urlParams.toString()}`
        : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
      return langParam;
    }
  } catch {}

  // 2. Cookie kontrol (NEXT_LOCALE - website'den gelen)
  try {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [key, value] = cookie.trim().split('=');
      if (key === 'NEXT_LOCALE' && (value === 'en' || value === 'tr')) {
        localStorage.setItem('language', value);
        return value;
      }
    }
  } catch {}

  // 3. localStorage kontrol
  try {
    const stored = localStorage.getItem('language');
    if (stored === 'en' || stored === 'tr') {
      return stored;
    }
  } catch {}

  // 4. Varsayılan
  return 'tr';
}

/**
 * Dil değiştirme fonksiyonu
 */
export function changeLanguage(lang: SupportedLanguage) {
  localStorage.setItem('language', lang);
  i18n.changeLanguage(lang);
}

/**
 * Mevcut dili getir
 */
export function getCurrentLanguage(): SupportedLanguage {
  return (i18n.language as SupportedLanguage) || 'tr';
}

/**
 * i18n başlatma fonksiyonu
 * Her panel kendi main.tsx'inde çağırır: initI18n('user')
 */
export function initI18n(panel: PanelType) {
  const panelLocale = panelResources[panel];

  i18n.use(initReactI18next).init({
    resources: {
      tr: {
        common: commonTr,
        auth: authTr,
        enums: enumsTr,
        panel: panelLocale.tr,
      },
      en: {
        common: commonEn,
        auth: authEn,
        enums: enumsEn,
        panel: panelLocale.en,
      },
    },
    lng: detectLanguage(),
    fallbackLng: 'tr',
    defaultNS: 'panel',
    ns: ['common', 'auth', 'enums', 'panel'],
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense: false,
    },
  });

  return i18n;
}

export default i18n;
